import { Children, cloneElement, createContext, isValidElement } from "react";
import { useContext, useMemo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "./AssistantMarkdown.module.scss";

// Money/percentage/plain figures, e.g. "₦6,911.74", "-12", "3.5%"
const NUMERIC_CELL = /^[₦$€£]?\s*-?[\d,]+(\.\d+)?\s*%?$/;
// Long digit runs are identifiers (customer IDs), not quantities
const ID_CELL = /^\d{8,}$/;

const TableColumnsContext = createContext({
  numericColumns: new Set(),
  idColumns: new Set(),
});

const nodeText = (node) => {
  if (!node) return "";
  if (node.type === "text") return node.value || "";
  return (node.children || []).map(nodeText).join("");
};

const elementChildren = (node, tagNames) =>
  (node?.children || []).filter(
    (child) =>
      child.type === "element" &&
      (!tagNames || tagNames.includes(child.tagName)),
  );

const bodyRowsOf = (tableNode) => {
  const sections = elementChildren(tableNode, ["thead", "tbody", "tfoot"]);
  const bodies = sections.filter((section) => section.tagName === "tbody");
  const rows = (bodies.length ? bodies : sections).flatMap((section) =>
    elementChildren(section, ["tr"]),
  );
  return rows.map((row) => elementChildren(row, ["td", "th"]).map(nodeText));
};

// A column counts as numeric/id only when every value in it looks the part, so
// a stray label never right-aligns a text column.
const analyzeTable = (tableNode) => {
  const rows = bodyRowsOf(tableNode);
  const numericColumns = new Set();
  const idColumns = new Set();
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);

  for (let column = 0; column < columnCount; column++) {
    const values = rows
      .map((row) => (row[column] || "").trim())
      .filter((value) => value !== "" && value !== "-");
    if (!values.length) continue;

    if (values.every((value) => ID_CELL.test(value))) idColumns.add(column);
    else if (values.every((value) => NUMERIC_CELL.test(value)))
      numericColumns.add(column);
  }

  return { numericColumns, idColumns };
};

const MarkdownTable = ({ node, children, ...props }) => {
  const columns = useMemo(() => analyzeTable(node), [node]);

  return (
    <TableColumnsContext.Provider value={columns}>
      <div className={styles.assistantMd__tableWrap}>
        <table {...props}>{children}</table>
      </div>
    </TableColumnsContext.Provider>
  );
};

// Cells get their column class here, where the index is known.
// eslint-disable-next-line no-unused-vars
const MarkdownRow = ({ node, children, ...props }) => {
  const { numericColumns, idColumns } = useContext(TableColumnsContext);
  let column = -1;

  return (
    <tr {...props}>
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
        column++;

        const className = numericColumns.has(column)
          ? styles.assistantMd__cellNum
          : idColumns.has(column)
            ? styles.assistantMd__cellId
            : undefined;

        return className
          ? cloneElement(child, {
              className: [child.props.className, className]
                .filter(Boolean)
                .join(" "),
            })
          : child;
      })}
    </tr>
  );
};

// eslint-disable-next-line no-unused-vars
const MarkdownLink = ({ node, children, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const markdownComponents = {
  a: MarkdownLink,
  table: MarkdownTable,
  tr: MarkdownRow,
};

const AssistantMarkdown = ({ content }) => (
  <div className={styles.assistantMd}>
    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </Markdown>
  </div>
);

export default AssistantMarkdown;
